import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * `<DotloomEditor>`: a complete reference editor. Every part is also exported
 * separately so hosts can compose their own layout around `EditorProvider`.
 */
import { Autosave, bindDom, builtinTools, DotloomEngine, EditorCore, IndexedDbStorage, isValidCamera, LENGTH_UNITS, MemoryStorage, NullViewport, PluginHost, Viewport, } from '@dotloomjs/sdk';
import { useCallback, useEffect, useMemo, useRef, useState, } from 'react';
import { buildActions } from './actions.js';
import { CommandPalette } from './components/CommandPalette.js';
import { ConstraintsPanel } from './components/ConstraintsPanel.js';
import { ErrorBanner, MissingPluginsBanner, RecoveryDialog } from './components/Dialogs.js';
import { Inspector } from './components/Inspector.js';
import { Icon } from './components/icons.js';
import { LayersPanel } from './components/LayersPanel.js';
import { ObjectsPanel } from './components/ObjectsPanel.js';
import { PluginPanels } from './components/PluginPanels.js';
import { StatusBar } from './components/StatusBar.js';
import { Toolbar } from './components/Toolbar.js';
import { EditorProvider, useDocument, useEditor, useEditorState } from './context.js';
import { I18nProvider, useT } from './i18n.js';
function useResolvedTheme(mode) {
    const query = typeof matchMedia === 'function' ? matchMedia('(prefers-color-scheme: dark)') : null;
    const [dark, setDark] = useState(query?.matches ?? false);
    useEffect(() => {
        if (!query)
            return;
        const on = () => setDark(query.matches);
        query.addEventListener('change', on);
        return () => query.removeEventListener('change', on);
    }, [query]);
    return mode === 'system' ? (dark ? 'dark' : 'light') : mode;
}
function Shell(props) {
    const h = useEditor();
    const t = useT();
    const doc = useDocument();
    const unit = doc?.settings?.displayUnit ?? 'millimetre';
    const tool = useEditorState((s) => s.tool);
    const canUndo = useEditorState((s) => s.canUndo);
    const canRedo = useEditorState((s) => s.canRedo);
    const [palette, setPalette] = useState(false);
    const [uiError, setUiError] = useState(() => props.started.loadError ? { title: t('error.load'), message: props.started.loadError } : null);
    const nameRef = useRef(props.name);
    nameRef.current = props.name;
    useEffect(() => {
        h.core.setUnitScale(LENGTH_UNITS[unit].mm);
    }, [h.core, unit]);
    const hooks = useMemo(() => ({
        name: () => nameRef.current,
        setName: (n) => {
            props.setName(n);
            h.autosave?.setName(n);
        },
        error: (title, e) => setUiError({ title, message: e instanceof Error ? e.message : String(e) }),
        missing: (m) => props.setMissing(m),
        view: () => ({ camera: h.viewport.camera }),
        restoreView: (v) => {
            const cam = v?.camera;
            if (cam && isValidCamera(cam))
                h.viewport.setCamera(cam);
            else
                void h.viewport.fit();
        },
    }), [h, props.setName, props.setMissing]);
    // biome-ignore lint/correctness/useExhaustiveDependencies: tools/plugins change rarely; rebuilt when the palette opens
    const actions = useMemo(() => buildActions(h, t, hooks), [h, t, hooks, palette]);
    const run = useCallback((id) => void actions.find((a) => a.id === id)?.run(), [actions]);
    const empty = doc !== null && doc.entities.length === 0;
    // Editor-wide shortcuts (palette, save, open) work anywhere inside the editor,
    // including panels; tool shortcuts stay on the canvas surface (see bindDom).
    const runRef = useRef(run);
    runRef.current = run;
    useEffect(() => {
        const el = props.root.current;
        if (!el)
            return;
        const listener = (e) => {
            const mod = e.ctrlKey || e.metaKey;
            const k = e.key.toLowerCase();
            if (mod && k === 'k') {
                e.preventDefault();
                setPalette(true);
            }
            else if (mod && k === 's') {
                e.preventDefault();
                runRef.current('file.save');
            }
            else if (mod && k === 'o') {
                e.preventDefault();
                runRef.current('file.open');
            }
        };
        el.addEventListener('keydown', listener);
        return () => el.removeEventListener('keydown', listener);
    }, [props.root]);
    return (_jsxs(_Fragment, { children: [_jsxs("header", { className: "dl-topbar", children: [_jsxs("fieldset", { className: "dl-row dl-plain-fieldset", children: [_jsx("legend", { className: "dl-visually-hidden", children: t('menu.file') }), _jsx("button", { type: "button", className: "dl-btn", onClick: () => run('file.new'), children: t('menu.new') }), _jsx("button", { type: "button", className: "dl-btn", onClick: () => run('file.open'), children: t('menu.open') }), _jsx("button", { type: "button", className: "dl-btn", onClick: () => run('file.save'), children: t('menu.save') }), _jsx("button", { type: "button", className: "dl-btn", onClick: () => run('file.exportSvg'), children: t('menu.exportSvg') }), _jsx("button", { type: "button", className: "dl-btn", onClick: () => run('file.exportDxf'), children: t('menu.exportDxf') }), h.canvas && (_jsx("button", { type: "button", className: "dl-btn", onClick: () => run('file.exportPng'), children: t('menu.exportPng') }))] }), _jsx("span", { className: "dl-muted", "data-testid": "document-name", children: props.name }), _jsx("span", { className: "dl-spacer" }), _jsx("button", { type: "button", className: "dl-btn dl-icon-btn", "aria-label": t('menu.undo'), title: `${t('menu.undo')} (Ctrl+Z)`, disabled: !canUndo, onClick: () => void h.core.undo(), children: _jsx(Icon, { name: "undo" }) }), _jsx("button", { type: "button", className: "dl-btn dl-icon-btn", "aria-label": t('menu.redo'), title: `${t('menu.redo')} (Ctrl+Shift+Z)`, disabled: !canRedo, onClick: () => void h.core.redo(), children: _jsx(Icon, { name: "redo" }) }), _jsxs("button", { type: "button", className: "dl-btn", onClick: () => setPalette(true), title: "Ctrl+K", children: [_jsx(Icon, { name: "palette" }), " ", t('menu.commands')] }), _jsxs("select", { className: "dl-select", style: { width: 'auto' }, "aria-label": t('menu.theme'), value: props.themeMode, onChange: (e) => props.setThemeMode(e.target.value), children: [_jsx("option", { value: "light", children: t('theme.light') }), _jsx("option", { value: "dark", children: t('theme.dark') }), _jsx("option", { value: "system", children: t('theme.system') })] })] }), _jsx(Toolbar, {}), _jsxs("div", { className: "dl-canvas-overlay", children: [_jsx(ErrorBanner, { extra: uiError, onDismissExtra: () => setUiError(null) }), _jsx(MissingPluginsBanner, { types: props.missing, onDismiss: () => props.setMissing([]) }), props.started.gpuError && (_jsxs("div", { className: "dl-canvas-message dl-error", role: "alert", children: [_jsx("strong", { children: t('error.gpu.title') }), _jsx("p", { children: t('error.gpu.body') }), _jsxs("details", { children: [_jsx("summary", { children: t('error.details') }), _jsx("ul", { children: props.started.attempts.map((a) => (_jsxs("li", { children: [a.backend, ": ", a.error] }, a.backend))) })] })] })), empty && tool === 'select' && !props.started.gpuError && (_jsxs("div", { className: "dl-canvas-message", role: "note", children: [_jsx("strong", { children: t('empty.title') }), _jsx("p", { className: "dl-muted", children: t('empty.body') })] }))] }), _jsxs("aside", { className: "dl-sidebar", "aria-label": t('panel.inspector'), children: [_jsx(Inspector, {}), _jsx(ConstraintsPanel, {}), _jsx(LayersPanel, {}), _jsx(ObjectsPanel, {}), _jsx(PluginPanels, {}), props.children] }), _jsx(StatusBar, { unit: unit }), palette && _jsx(CommandPalette, { actions: actions, onClose: () => setPalette(false) }), props.recovery && (_jsx(RecoveryDialog, { meta: props.recovery, onRestore: () => {
                    const a = h.autosave;
                    props.setRecovery(null);
                    void a?.restore().then((v) => hooks.restoreView(v), (e) => setUiError({ title: t('error.load'), message: String(e) }));
                }, onDiscard: () => {
                    props.setRecovery(null);
                    void h.autosave?.discard();
                } }))] }));
}
/** The complete editor. Give it a sized container (it fills 100% height). */
export function DotloomEditor(props) {
    const [themeMode, setThemeMode] = useState(props.theme ?? 'system');
    const theme = useResolvedTheme(themeMode);
    const host = useRef(null);
    const rootRef = useRef(null);
    const [started, setStarted] = useState(null);
    const [fatal, setFatal] = useState(null);
    const [name, setName] = useState(props.name ?? 'untitled.dotl');
    const [missing, setMissing] = useState([]);
    const [recovery, setRecovery] = useState(null);
    const initial = useRef({ props, theme });
    useEffect(() => {
        const p = initial.current.props;
        const el = host.current;
        if (!el)
            return;
        let cancelled = false;
        const cleanup = [];
        // Register a disposer; if the effect was already cleaned up (StrictMode,
        // fast unmount) dispose right away so nothing created late leaks.
        const own = (dispose) => {
            if (cancelled) {
                dispose();
                return false;
            }
            cleanup.push(dispose);
            return true;
        };
        void (async () => {
            const engine = await DotloomEngine.create(p.engine ?? {});
            if (!own(() => engine.dispose()))
                return;
            let canvas = null;
            let gpuError = null;
            let attempts = [];
            try {
                const v = await Viewport.create(el, engine, { ...p.viewport, theme: { preset: initial.current.theme } });
                if (!own(() => v.dispose()))
                    return;
                canvas = v;
            }
            catch (e) {
                gpuError = e instanceof Error ? e.message : String(e);
                attempts = (e.details?.attempts ??
                    []);
            }
            if (cancelled)
                return;
            const viewport = canvas ?? new NullViewport();
            const core = new EditorCore(engine, viewport, p.core);
            for (const tool of builtinTools())
                core.registerTool(tool);
            core.start();
            own(() => core.dispose());
            if (canvas)
                own(bindDom(core, canvas, { label: 'Drawing canvas' }));
            const plugins = new PluginHost(engine, core);
            own(() => void plugins.dispose());
            for (const pl of p.plugins ?? []) {
                try {
                    await plugins.register(pl);
                }
                catch (e) {
                    core.report(e);
                }
            }
            let autosave = null;
            if (p.autosave !== false) {
                const opt = typeof p.autosave === 'object' ? p.autosave : {};
                const storage = opt.storage ??
                    plugins.storageAdapters()[0] ??
                    (typeof indexedDB === 'undefined' ? new MemoryStorage() : new IndexedDbStorage());
                const a = new Autosave(engine, storage, {
                    ...(opt.key ? { key: opt.key } : {}),
                    name: p.name ?? 'untitled.dotl',
                    view: () => ({ camera: viewport.camera }),
                });
                own(() => a.dispose());
                autosave = a;
            }
            let loadError = null;
            if (p.document) {
                try {
                    const r = await engine.load(p.document);
                    setMissing(r.missingPlugins);
                    await viewport.fit();
                }
                catch (e) {
                    loadError = e instanceof Error ? e.message : String(e);
                }
            }
            else if (autosave) {
                const rec = await autosave.recoverable().catch(() => null);
                if (rec && !cancelled)
                    setRecovery(rec);
            }
            if (cancelled)
                return;
            autosave?.start();
            const handle = { engine, core, viewport, canvas, plugins, autosave };
            setStarted({ handle, gpuError, attempts, loadError });
            p.onReady?.(handle);
        })().catch((e) => {
            if (!cancelled)
                setFatal(e instanceof Error ? e.message : String(e));
        });
        return () => {
            cancelled = true;
            for (const f of cleanup.splice(0).reverse())
                f();
        };
    }, []);
    useEffect(() => {
        started?.handle.canvas?.setTheme({ preset: theme });
    }, [started, theme]);
    const cls = ['dl-editor', props.className].filter(Boolean).join(' ');
    return (_jsx(I18nProvider, { ...(props.locale ? { locale: props.locale } : {}), ...(props.messages ? { messages: props.messages } : {}), children: _jsxs("div", { className: cls, style: props.style, "data-theme": theme, ref: rootRef, children: [_jsx("div", { className: "dl-canvas", children: _jsx("div", { ref: host, className: "dl-canvas-host" }) }), fatal && (_jsx("div", { className: "dl-canvas-overlay", children: _jsx("div", { className: "dl-banner dl-error", role: "alert", style: { margin: 16 }, children: fatal }) })), started && (_jsx(EditorProvider, { value: started.handle, children: _jsx(Shell, { started: started, theme: theme, themeMode: themeMode, setThemeMode: setThemeMode, name: name, setName: setName, missing: missing, setMissing: setMissing, recovery: recovery, setRecovery: setRecovery, root: rootRef, children: props.children }) }))] }) }));
}
//# sourceMappingURL=DotloomEditor.js.map